import React, { useCallback, useEffect, useRef, useState } from "react";
import ApiService from "../../services/Api.service";
import Alert from "../Alert/Alert";
import "./VoiceAnalyticsAiModel.css"
import AudioPlayer from "../AudioPlayerModel/AudioPlayer";
import QualityCheckForm from "./QualityCheckForm";
import AgentTreaningForm from "./AgentTrainingForm";
import AuthService from "../../services/Auth.service";
import Skeleton from "../Skeleton/Skeleton";
import { Headset, Languages, UserCircle, Sparkles, ClipboardCheck } from "lucide-react";

const VoiceAnalyticsAiModel = ({ showModal, closeModal, item}) => {
    const modalBodyRef = useRef(null);
    const [data, setData] = useState("");
    const [policyDetails, setPolicyDetails] = useState("");
    const [currentTime, setCurrentTime] = useState(0);
    const [aiAnalizeData, setAiAnalizeData] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Recording Details');
    const [activeTab2, setActiveTab2] = useState('Quality Audit');
    const [tabs, setTabs] = useState(['Recording Details', 'Transcription']);
    const [tabs2, setTabs2] = useState(["Quality Audit", "AI Summary"]);
    const [expandedParamKey, setExpandedParamKey] = useState(null);
    const [expandedSectionIndex, setExpandedSectionIndex] = useState(0);
    const [formData, setFormData] = useState([]);
    const [updatedQualityParams, setUpdatedQualityParams] = useState({});
    const [showPopup, setShowPopup] = useState(false);
    const [showReanalyzeConfirm, setShowReanalyzeConfirm] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [user, setUser] = useState(null);
    const [visible, setVisible] = useState(true);
    const [originalTranscription, setOriginalTranscription] = useState([]);
    const [transcriptionBlock, setTranscriptionBlock] = useState([]); // current visible
    const [showEnglish, setShowEnglish] = useState(false);
    const [translatedTranscription, setTranslatedTranscription] = useState([]);
    const [audioUrl, setAudioUrl] = useState("");

    const [alertValue, setAlertValue] = useState({
        show: false,
        message: "",
        variant: "success",
        position: "middle",
    });

    useEffect(() => {
        const fetchAudio = async () => {
            const callId = item?.id || item?._id;
            if (!callId) return;
            try {
                const res = await ApiService.getRecordingSignedUrl(callId);
                if (res?.data?.data) {
                    setAudioUrl(res.data.data);
                }
            } catch (err) {
                console.warn("Could not fetch recording url from calls-record:", err);
            }
        };

        if (showModal && (item?.id || item?._id)) {
            fetchAudio();
        } else {
            setAudioUrl("");
        }
    }, [showModal, item]);

    useEffect(() => {
        setUser(AuthService.getCurrentUser());
    }, []);

    useEffect(() => {
        if (modalBodyRef.current) {
            modalBodyRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [showModal, item]);

    const applyDataToState = (resData) => {
        const d = resData || {};
        setData(d);
        setPolicyDetails(d?.policy_details || {});
        setAiAnalizeData(d?.quality_details?.ai_summary || {});
        setUpdatedQualityParams(d?.quality_details?.quality_check || {});
        setFormData(d?.quality_details?.quality_check?.sections || []);
        setTranscriptionBlock(d?.transcription_block || []);
        setOriginalTranscription(d?.transcription_block || []);
        setTabs(['Recording Details', 'Transcription']);
        setTabs2(["Quality Audit", "AI Summary"]);
    };

    const fetchDetailById = useCallback(async ({ id }) => {
        try {
            setLoading(true);
            let response = await ApiService.getTranscriptionDetailById(id);

            if (response.status === 200 && response.data) {
                applyDataToState(response.data);
            } else {
                applyDataToState(null);
                setAlertValue({
                    show: true,
                    message: "No details found for this call.",
                    variant: "warning",
                    position: "middle",
                });
            }
        } catch (error) {
            console.error("Failed to fetch call details:", error);
            applyDataToState(null);
            setAlertValue({
                show: true,
                message: error.response?.data?.detail || "Failed to load call details from API.",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (showModal) {
            const callId = item?.id || item?._id;
            if (callId) {
                fetchDetailById({ id: callId });
            } else {
                applyDataToState(null);
            }
        }
    }, [showModal, item, fetchDetailById]);
 
    const handleClose = () => {
        closeModal();
        setAiAnalizeData("")
        setLoading(true)
        setVisible(true)
        setFormData([])
        setExpandedSectionIndex(0)
        setActiveTab('Recording Details')
        setActiveTab2('Quality Audit')
        setTranslatedTranscription([]);
        setTranscriptionBlock([]);
        setShowEnglish(false);
    };

    const handleSubmit = async () => {
        const updated = updatedQualityParams;
        updated.sections = formData;
        const payload = {
            quality_check: updated,
            ai_summary: aiAnalizeData,
            audit_by: user?.name,
        };

        try {
            const res = await ApiService.updateQualityDetails(item?.id || item?._id, payload);
            if (res?.data?.accuracy_score !== undefined) {
                const newScore = res.data.accuracy_score;
                setData(prev => ({ ...prev, overall_score: newScore }));
                setAiAnalizeData(prev => ({ ...prev, cqa_score: `${newScore}%` }));
            }
            setAlertValue({
                show: true,
                message: "Quality details submitted successfully!",
                variant: "success",
                position: "middle",
            });
        } catch (error) {
            setAlertValue({
                show: true,
                message: error.response?.data?.detail || "Failed to submit quality details.",
                variant: "danger",
                position: "middle",
            });
        }
    };
    const formatLabel = (key) => {
        return key
            .replace(/_/g, " ")           // replace underscores with space
            .replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize words
    };

    const getRecordingDetailsList = () => {
        if (!data || typeof data !== "object") return [];

        const hasVal = (val) => val !== null && val !== undefined && val !== "" && val !== "---";

        const fields = [
            { key: "policy_number", label: "Policy Number", value: data?.policy_number },
            { key: "agent_name", label: "Agent Name", value: data?.agent_name },
            { key: "employee_code", label: "Employee Code", value: data?.employee_code },
            { key: "phone_no", label: "Phone No", value: data?.phone_no },
            { key: "recording_file", label: "Recording File", value: data?.recording_file || data?.recording_file_url?.split("/")?.pop()?.split("?")?.[0] },
            { key: "call_date", label: "Call Date", value: data?.call_date ? String(data.call_date).replace("T", " ") : null },
            { key: "duration", label: "Duration", value: data?.duration },
            { key: "disposition", label: "Disposition", value: data?.disposition || data?.ai_disposition, isBadge: true },
            { key: "word_count", label: "Word Count", value: data?.word_count },
            { key: "rate_of_speech", label: "Rate Of Speech", value: data?.rate_of_speech },
            { key: "sentiment", label: "Sentiment", value: data?.agent_sentiment?.label },
            { key: "agent_score", label: "Agent Score", value: data?.agent_sentiment?.score },
            { key: "customer_sentiment", label: "Customer Sentiment", value: data?.customer_sentiment?.label },
            { key: "customer_score", label: "Customer Score", value: data?.customer_sentiment?.score },
            { key: "accuracy_score", label: "Accuracy Score", value: data?.accuracy_score || data?.overall_score },
            { key: "audit_by", label: "Audit By", value: data?.audit_by },
            { key: "status", label: "Status", value: data?.status }
        ];

        return fields.filter(f => hasVal(f.value));
    };



    const handleTranslate = async () => {
        try {
            if (showEnglish) {
                setTranscriptionBlock(originalTranscription);
                setShowEnglish(false);
                return;
            }

            if (translatedTranscription.length > 0) {
                setTranscriptionBlock(translatedTranscription);
                setShowEnglish(true);
                return;
            }

            let response = await ApiService.getTranslate(item.id);

            if (response.status === 200 && response.data) {
                setTranslatedTranscription(response.data.result); // store in cache
                setTranscriptionBlock(response.data.result);       // show translated
                setShowEnglish(true);
            }

        } catch (error) {
            console.error("Error during translation:", error);
        }
    };

    return (
        <>
            {alertValue.show && (
                <Alert
                    variant={alertValue.variant}
                    message={alertValue.message}
                    dismissible={true}
                    timeout={2500}
                    position={alertValue.position}
                    onClose={() =>
                        setAlertValue({ show: false, message: "", variant: "" })
                    }
                />
            )}
            <div
                className={`modal fade ${showModal ? "show" : ""}`}
                style={{ display: showModal ? "block" : "none", overflowY: "auto" }}
            >
                <div className="modal-dialog-ai" >
                    <div className="modal-content container ai-container" style={{ maxHeight: "100%" }}>
                        <div className="ai-modal-header">
                            <div className="model-header-container">
                                <p className="model-heading">Call Details</p> <span className="hz-line"></span>
                                <div className="hover-dialog-wrapper">
                                    <div
                                        onMouseEnter={() => setShowPopup(true)}
                                        onMouseLeave={() => setShowPopup(false)}
                                        className="hover-trigger"
                                    >
                                        <p className="model-labels" >#{data?.policy_number} </p>
                                    </div>
                                    {showPopup && (
                                        <div className="hover-popup">
                                            {policyDetails && (
                                                <>
                                                    {Object.entries(policyDetails).map(([key, value]) => (
                                                        value && (
                                                            <div key={key}>
                                                                <strong>{formatLabel(key)}:</strong> {value}
                                                            </div>
                                                        )
                                                    ))}
                                                </>
                                            )}
                                        </div>

                                    )}
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <button
                                type="button"
                                className="close-btn"
                                data-dismiss="modal"
                                onClick={handleClose}
                                style={{ color: "red" }}
                              >
                                &times;
                              </button>
                            </div>
                        </div>

                        <div
                            className="modal-body"
                            ref={modalBodyRef}
                            style={{ display: "flex", gap: "10px", flex: 1, overflow: "hidden", padding: "6px 10px" }}
                        >

                            <div className="left-ai-model-container">
                                {loading ? (
                                    <Skeleton showLeft={true} showRight={false} />

                                ) : (<>
                                    <div className="left-ai-model-child-container">
                                        <div className="audio-parent-container">
                                            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                                <div className="audio-container">
                                                    <AudioPlayer
                                                        url={audioUrl || data?.recording_file_url}
                                                        callId={item?.id || item?._id || data?.id}
                                                        onTimeUpdate={(time) => setCurrentTime(time)}
                                                    />

                                                </div>
                                            </div>
                                            <div>
                                            </div>
                                        </div>

                                        <div className="tabs-container">
                                            <div className="tabs">
                                                {tabs.map((tab) => (
                                                    <div
                                                        key={tab}
                                                        className={`tab ${activeTab === tab ? 'active' : ''}`}
                                                        onClick={() => setActiveTab(tab)}
                                                    >
                                                        {tab === 'Ask AI' && <Sparkles size={14} className="ai-icon" />}
                                                        {tab}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="tab-content">
                                                {activeTab === 'Recording Details' && (
                                                    <div className="recording-details">
                                                        <div className="recording-table-container">
                                                            <table className="recording-table">
                                                                <tbody>
                                                                    {getRecordingDetailsList().map((item, idx) => (
                                                                        <tr key={idx}>
                                                                            <td>{item.label}:</td>
                                                                            <td>
                                                                                {item.isBadge ? (
                                                                                    <span className="disposition-positive">{item.value}</span>
                                                                                ) : (
                                                                                    <span>{item.value}</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                                {activeTab === "Transcription" && (
                                                    <>



                                                        <div className="transcription-content">
                                                            <div className="chat-container">

                                                                {/*  If transcription_block exists → show chat format with highlight */}
                                                                {transcriptionBlock?.length > 0 ? (
                                                                    transcriptionBlock.map((block, index) => {
                                                                        const isActive =
                                                                            currentTime >= block.start_time && currentTime <= block.end_time;

                                                                        return (
                                                                            <div
                                                                                key={index}
                                                                                className={`chat-message ${block.role === "agent" ? "left" : "right"}`}
                                                                            >
                                                                                <div className="avatar">
                                                                                    {block.role === "agent" ? <Headset /> : <UserCircle />}
                                                                                </div>

                                                                                <div className="chat-bubble">
                                                                                    <span className="role-label">
                                                                                        {block.role === "agent" ? "Agent" : "Customer"}
                                                                                    </span>

                                                                                    {/*  highlight text only */}
                                                                                    <p className={isActive ? "highlight-text" : ""}>
                                                                                        {block.text}
                                                                                    </p>

                                                                                    <span className="timestamp">
                                                                                        {typeof block.start_time === "number"
                                                                                            ? block.start_time.toFixed(2)
                                                                                            : ""}
                                                                                        {typeof block.end_time === "number"
                                                                                            ? block.end_time.toFixed(2)
                                                                                            : ""}
                                                                                    </span>

                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    /*  If no transcription_block → show full paragraph transcription */
                                                                    <p
                                                                        style={{
                                                                            textAlign: "justify",
                                                                            fontSize: "14px",
                                                                        }}
                                                                    >
                                                                        {data?.transcription || "No transcription available"}
                                                                    </p>
                                                                )}

                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                </>)}
                            </div>
                            <div className="right-ai-model-container">
                                {loading ? (
                                    <Skeleton showLeft={false} showRight={true} />

                                ) : (<>
                                    <div className="tabs-container">

                                        <div className="tabs">
                                            {tabs2.map((tab) => {
                                                const getTabIcon = () => {
                                                    switch (tab) {
                                                        case "Quality Audit":
                                                            return <ClipboardCheck size={16} className="ai-icon" />;
                                                        case "AI Summary":
                                                            return <Sparkles size={16} className="ai-icon" />;
                                                        default:
                                                            return <Sparkles size={16} className="ai-icon" />;
                                                    }
                                                };
                                                return (
                                                    <div
                                                        key={tab}
                                                        className={`tab ${activeTab2 === tab ? 'active' : ''}`}
                                                        onClick={() => setActiveTab2(tab)}
                                                    >
                                                        {getTabIcon()}
                                                        {tab}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="tab-content">
                                            {activeTab2 === 'Quality Audit' &&
                                                <QualityCheckForm setExpandedSectionIndex={setExpandedSectionIndex} expandedSectionIndex={expandedSectionIndex}
                                                    setExpandedParamKey={setExpandedParamKey} expandedParamKey={expandedParamKey}
                                                    setVisible={setVisible} visible={visible} setActiveTab2={setActiveTab2} formData={formData} setFormData={setFormData} />
                                            }
                                            {activeTab2 === 'AI Summary' && (
                                                <AgentTreaningForm
                                                    handleSubmit={handleSubmit}
                                                    setAlertValue={setAlertValue}
                                                    aiAnalizeData={aiAnalizeData}
                                                    setAiAnalizeData={setAiAnalizeData}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </>)}

                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default VoiceAnalyticsAiModel;
